/*
 * Copyright (c) 2023 Powerledger Pty Ltd. All Rights Reserved.
 */

import "react-toastify/dist/ReactToastify.css";

import { Lottie } from "@alfonmga/react-lottie-light-ts";
import { Box, Button, Flex, Link, Modal, Text } from "@powerledger/ui-component-lib";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { getSupportEmail } from "@/app/lib/get-support-email";
import registerSuccess from "@/assets/animations/registerSuccess.json";

export const OnboardingCompletedModal = ({ visible, closeModal }: { visible: boolean; closeModal: () => void }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const supportEmail = getSupportEmail();
  const options = {
    animationData: registerSuccess,
    loop: true,
    autoplay: true,
  };
  return (
    <Modal
      visible={visible}
      onCancel={closeModal}
      sx={{ maxHeight: "inherit" }}
      hideCloseButton
      modalMaxWidth={["90vw", "70vw", "50vw"]}
      outerContent={{
        position: "bottom",
        node: (
          <Box
            sx={{
              width: "40%",
              display: ["none", "flex"],
              position: "absolute",
              left: [-40, -25],
              top: [-140, -90],
              bottom: 0,
              alignItems: "center",
            }}
          >
            <Lottie config={options} />
          </Box>
        ),
      }}
    >
      <>
        <Flex sx={{ justifyContent: "center", pb: [3, 5] }}>
          <Box sx={{ width: ["auto", "60%"], ml: ["auto", "40%"] }}>
            <Text sx={{ fontSize: [3, null, 5], fontFamily: "MintGroteskV08", fontWeight: "bold" }}>
              {t("Your application has been received!")}
            </Text>
            <Flex sx={{ mt: 4 }}>
              <Text sx={{ fontFamily: "MintGroteskV08", fontWeight: "bold", fontSize: [0, 2] }}>
                <Trans>
                  The TraceX team will start reviewing your application soon and will get in touch if any additional
                  information is required. In the meantime, please accept the market invite on your M-RETS account
                  dashboard and review the TraceX Account Settings page to ensure your application is complete. Contact
                  us at{" "}
                  <Link sx={{ color: "primary" }} target="_blank" rel="noreferrer" href={`mailto:${supportEmail}`}>
                    {{ supportEmail }}
                  </Link>{" "}
                  for any queries with regards to your application.
                </Trans>
              </Text>
            </Flex>
          </Box>
        </Flex>
        <Flex sx={{ justifyContent: "flex-end" }}>
          <Button
            type="submit"
            variant="primary"
            onClick={() => {
              closeModal();
              navigate("/settings");
            }}
            data-testid={"onboarding-completed--browse"}
          >
            {t("Browse")}
          </Button>
        </Flex>
      </>
    </Modal>
  );
};
